import { useCallback, useRef } from 'react';
import { useGlobalStateContext } from '@/context/GlobalContext';
import ActiveProjectsService from '@/services/activeProjectsService';
import { getGenericValueCase } from '@/utils/helper';
import { pickFirstProductLevelStatusString } from '@/utils/activeProjectProductUtils';

const inFlightByProjectId = new Map<string, Promise<any | null>>();

export const useLoadProjectDetails = () => {
  const {
    setSelectedActiveProject,
    setActiveProjectsProductInfo,
    setProductInfoCreationDateFromDetails,
    clearProductInfoCreationDate,
    productInfoCreationDateIdeaId,
  } = useGlobalStateContext();
  const settersRef = useRef({
    setSelectedActiveProject,
    setActiveProjectsProductInfo,
    setProductInfoCreationDateFromDetails,
    clearProductInfoCreationDate,
  });
  settersRef.current = {
    setSelectedActiveProject,
    setActiveProjectsProductInfo,
    setProductInfoCreationDateFromDetails,
    clearProductInfoCreationDate,
  };
  const ideaIdRef = useRef(productInfoCreationDateIdeaId);
  ideaIdRef.current = productInfoCreationDateIdeaId;

  const loadProjectDetails = useCallback(async (projectId: string | number): Promise<any | null> => {
    const key = String(projectId);
    const existing = inFlightByProjectId.get(key);
    if (existing) {
      return existing;
    }

    // Avoid leaking previous product's creationDate into recipe UI while a different product loads
    if (String(ideaIdRef.current ?? "") !== key) {
      settersRef.current.clearProductInfoCreationDate();
    }

    const run = (async (): Promise<any | null> => {
      try {
        const response = await ActiveProjectsService.getProductInfoDetails(key);

        if (response) {
          const record = response as Record<string, unknown>;
          const statusSource =
            pickFirstProductLevelStatusString(record) || String((response as any)?.businessStatus ?? '');
          const projectData = {
            ...response,
            businessStatus: getGenericValueCase(statusSource),
          };
          const {
            setSelectedActiveProject: setSel,
            setActiveProjectsProductInfo: setInfo,
            setProductInfoCreationDateFromDetails: setCreationDate,
          } = settersRef.current;
          setSel(projectData);
          setInfo(projectData);
          // Side-effect only for recipe re-sort visibility; does not alter project payloads
          setCreationDate(projectData, key);

          return projectData;
        }

        return null;
      } catch (error) {
        console.error('❌ Error loading project details:', error);
        return null;
      } finally {
        inFlightByProjectId.delete(key);
      }
    })();

    inFlightByProjectId.set(key, run);
    return run;
  }, []);

  return { loadProjectDetails };
};

export default useLoadProjectDetails;
